import { Expert, ExpertField } from '@/models/Expert';
import { supabase } from './supabase';
import { expertService } from './expertService';
import { logger } from '@/utils/logger';

/**
 * Service để quản lý việc gán chuyên gia cho khóa học
 */
export const courseExpertService = {
  /**
   * Lấy chuyên gia được gán cho một khóa học cụ thể
   * @param courseId ID của khóa học
   * @returns Thông tin chuyên gia hoặc null nếu không tìm thấy
   */
  async getExpertForCourse(courseId: string): Promise<Expert | null> {
    try {
      logger.info(`Fetching expert for course: ${courseId}`);
      
      // Lấy expert_id từ bảng courses
      const { data, error } = await supabase
        .from('courses')
        .select('expert_id')
        .eq('id', courseId)
        .single();
      
      if (error) {
        logger.error(`Error fetching expert_id for course ${courseId}:`, error);
        return null;
      }
      
      if (!data || !data.expert_id) {
        logger.info(`No expert assigned to course ${courseId}`);
        return null;
      }
      
      // Lấy thông tin chi tiết của expert từ bảng experts
      return await expertService.getExpertById(data.expert_id);
    } catch (error) {
      logger.error(`Unexpected error in getExpertForCourse for ${courseId}:`, error);
      return null;
    }
  },

  /**
   * Gán một chuyên gia cho một khóa học
   * @param courseId ID của khóa học
   * @param expertId ID của chuyên gia
   * @returns true nếu thành công, false nếu thất bại
   */
  async assignExpertToCourse(courseId: string, expertId: string): Promise<boolean> {
    try {
      logger.info(`Assigning expert ${expertId} to course ${courseId}`);
      
      // Kiểm tra xem expert có tồn tại không
      const expert = await expertService.getExpertById(expertId);
      if (!expert) {
        logger.error(`Expert ${expertId} not found`);
        return false;
      }
      
      // Cập nhật expert_id trong bảng courses
      const { error } = await supabase
        .from('courses')
        .update({ expert_id: expertId })
        .eq('id', courseId);
      
      if (error) {
        logger.error(`Error assigning expert to course:`, error);
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error(`Unexpected error in assignExpertToCourse:`, error);
      return false;
    }
  },

  /**
   * Xóa chuyên gia khỏi một khóa học
   * @param courseId ID của khóa học
   * @returns true nếu thành công, false nếu thất bại
   */
  async removeExpertFromCourse(courseId: string): Promise<boolean> {
    try {
      logger.info(`Removing expert from course ${courseId}`);
      
      // Cập nhật expert_id thành null trong bảng courses
      const { error } = await supabase
        .from('courses')
        .update({ expert_id: null })
        .eq('id', courseId);
      
      if (error) {
        logger.error(`Error removing expert from course:`, error);
        return false;
      }
      
      return true;
    } catch (error) {
      logger.error(`Unexpected error in removeExpertFromCourse:`, error);
      return false;
    }
  },
  
  /**
   * Lấy chuyên gia phù hợp cho một khóa học dựa trên thứ tự ưu tiên:
   * 1. Chuyên gia được gán trực tiếp cho khóa học
   * 2. Chuyên gia mặc định cho lĩnh vực của khóa học
   * 
   * @param courseId ID của khóa học
   * @param field Lĩnh vực của khóa học
   * @returns Chuyên gia phù hợp
   */
  async getBestExpertForCourse(
    courseId: string, 
    field: ExpertField = ExpertField.GENERAL
  ): Promise<Expert> {
    try {
      logger.info(`Getting best expert for course: courseId=${courseId}, field=${field}`);
      
      // 1. Thử lấy chuyên gia được gán trực tiếp cho khóa học
      const courseExpert = await courseExpertService.getExpertForCourse(courseId);
      if (courseExpert) {
        logger.info(`Using expert assigned to course: ${courseExpert.name}`);
        return courseExpert;
      }
      
      // 2. Lấy chuyên gia mặc định cho lĩnh vực
      logger.info(`Getting default expert for field: ${field}`);
      const defaultExpert = await expertService.getDefaultExpertForField(field);
      logger.info(`Using default expert for field: ${defaultExpert.name}`);
      return defaultExpert;
    } catch (error) {
      logger.error(`Error in getBestExpertForCourse for course ${courseId}:`, error);
      // Fallback: Lấy chuyên gia mặc định cho lĩnh vực
      logger.info(`Fallback: Getting default expert for field: ${field}`);
      return await expertService.getDefaultExpertForField(field);
    }
  }
}; 